import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Button, Col, Pagination, Row, Tooltip } from "antd";
import type { ColumnType } from "antd/es/table";
import moment from "moment";
import { useAppSelector } from "../..";
import {
  getDictionaryItemById,
  geDicValueById,
  displayFormat,
  uploadPriceFormat,
  checkIsLoading,
} from "../../lib/utils";
import type { DestinationType } from "../../store/slices/destinations/types";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import TablePageSelect from "../TablePageSelect/TablePageSelect";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_TC_ADMIN_DESTINATIONS_WRITE } from "../../lib/const/permissions";
import "./DestinationList.less";

interface Data {
  id: number;
  key: number;
  name: string;
  shortName: string;
  fullName: string;
  fullShortName: string; // ну вот такой нейминг, че. Как это еще назвать?
  destinationTypeName?: string;
  categoryName?: string;
  countryName?: string;
  regionName?: string;
  destinationSetName?: string;
  parentDestination?: string;
  areaCodesString: string;
  period: string;
}

const getDisplayFormatDate = (date?: string): string | undefined =>
  moment(date, uploadPriceFormat)?.format(displayFormat);

interface ComponentProps {
  onOpenVersions: (destinationId: number) => void;
  onCloseVersions: () => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
}

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const DestinationList = (props: ComponentProps) => {
  const { onOpenVersions, onCloseVersions, setPage, setSize } = props;
  const history = useHistory();
  const {
    destinationList,
    destinationListLoading,
    destinationSets,
    destinationListTotalElements,
    destinationVersions,
    destinationVersionsLoading,
    destinationListFilter,
  } = useAppSelector((state) => state.destinations);
  const { destinationTypes, destinationCategories, countries, regions } = useAppSelector(
    (state) => state.dicts
  );

  const [data, setData] = useState<Data[]>([]);
  const [versionsData, setVersionsData] = useState<Data[]>([]);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (rec) => {
    setHistoryModalVisible(true);
    setObjectId(rec.id);
  };

  const getTableData = (list: DestinationType[]): Data[] =>
    list.map((item) => {
      const {
        id,
        name,
        shortName,
        path,
        shortPath,
        destinationTypeId,
        categoryId,
        countryId,
        destinationSetId,
        parentDestinationId,
        areaCodes,
        fromDate,
        toDate,
      } = item;

      const fullName = [path, name].filter(Boolean).join(" - ");
      const fullShortName = [shortPath, shortName].filter(Boolean).join(" - ");
      const destinationTypeName = getDictionaryItemById(destinationTypes, destinationTypeId)?.name;
      const categoryName = getDictionaryItemById(destinationCategories, categoryId)?.name;
      const country = getDictionaryItemById(countries, countryId);
      const regionName = country ? getDictionaryItemById(regions, country.parentDicId)?.name : "";
      const destinationSetName = geDicValueById(destinationSets, destinationSetId)?.name;
      const parentDestination = getDictionaryItemById(regions, parentDestinationId)?.name;
      const fromDateString = fromDate ? getDisplayFormatDate(fromDate) : "";
      const toDateString = toDate ? getDisplayFormatDate(toDate) : "";
      const period = [fromDateString, toDateString].filter(Boolean).join(" - ");

      return {
        id,
        key: id,
        name,
        shortName,
        fullName,
        fullShortName,
        destinationTypeName,
        categoryName,
        countryName: country?.name,
        regionName,
        destinationSetName,
        parentDestination,
        areaCodesString: areaCodes.join(", "),
        period,
      };
    });

  // Данные списка направлений
  useEffect(() => {
    setData(getTableData(destinationList));
  }, [destinationList]);

  // Данные версий выбранного направления
  useEffect(() => {
    setVersionsData(getTableData(destinationVersions));
  }, [destinationVersions]);

  const columnsWidths = {
    name: 212,
    shortName: "9%",
    fullName: "9%",
    fullShortName: "9%",
    destinationTypeName: "11%",
    categoryName: "11%",
    countryName: "8%",
    regionName: "8%",
    destinationSetName: "8%",
    parentDestination: "8%",
    areaCodesString: "",
    period: "14%",
    buttons: "6%",
  };

  const [columns, setColumns] = useState<({ hidden?: boolean } & ColumnType<Data>)[]>([
    {
      title: "Наименование",
      key: "name",
      dataIndex: "name",
      width: columnsWidths.name,
    },
    {
      title: "Аббревиатура",
      key: "shortName",
      dataIndex: "shortName",
      width: columnsWidths.shortName,
    },
    {
      title: "Полное наименование",
      key: "fullName",
      dataIndex: "fullName",
      hidden: true,
      width: columnsWidths.fullName,
    },
    {
      title: "Полная аббревиатура",
      key: "fullShortName",
      dataIndex: "fullShortName",
      width: columnsWidths.fullShortName,
    },
    {
      title: "Тип",
      key: "destinationTypeName",
      dataIndex: "destinationTypeName",
      width: columnsWidths.destinationTypeName,
    },
    {
      title: "Категория",
      key: "categoryName",
      dataIndex: "categoryName",
      width: columnsWidths.categoryName,
    },
    {
      title: "Страна",
      key: "countryName",
      dataIndex: "countryName",
      width: columnsWidths.categoryName,
    },
    {
      title: "Регион",
      key: "regionName",
      dataIndex: "regionName",
      width: columnsWidths.regionName,
    },
    {
      title: "Набор",
      key: "destinationSetName",
      dataIndex: "destinationSetName",
      width: columnsWidths.destinationSetName,
    },
    {
      title: "Родительское направление",
      key: "parentDestination",
      dataIndex: "parentDestination",
      width: columnsWidths.parentDestination,
      render: (parentDestination) =>
        [parentDestination?.name, parentDestination?.shortName].filter(Boolean).join(" - "),
    },
    {
      title: "Коды",
      key: "areaCodesString",
      dataIndex: "areaCodesString",
      width: columnsWidths.areaCodesString,
      className: "areaCodesString",
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Период",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
      render: (value, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            showHistoryModal(record);
          }}
          className="iconButton"
          type="text"
          htmlType="button"
          icon={<DotsIcon />}
        />
      ),
    },
  ]);

  return (
    <div className="destination-list">
      <Row justify="space-between">
        <Col />
        <Col offset={5}>
          <Pagination
            onChange={setPage}
            current={destinationListFilter.page}
            total={destinationListTotalElements}
            pageSize={destinationListFilter.size}
            size="small"
            showSizeChanger={false}
            style={{ marginTop: "4px" }}
          />
        </Col>
        <Col>
          <TablePageSelect
            onChange={setSize}
            value={destinationListFilter.size}
            pageSizeOptions={{ 10: 10, 20: 20, 50: 50, 100: 100 }}
          />
        </Col>
      </Row>
      <ToggleableVersionsTable
        tableName="destination-list"
        tableData={data}
        columns={columns}
        childTableData={versionsData}
        childTableColumns={columns}
        isTableLoading={checkIsLoading(destinationListLoading)}
        isChildTableLoading={checkIsLoading(destinationVersionsLoading)}
        setColumns={setColumns}
        onOpenVersionsCallback={({ id }) => onOpenVersions(id)}
        onCloseVersionsCallback={onCloseVersions}
        onRow={(record) => ({
          onClick: () => {
            history.push(`/destination/${record.id}`);
          },
        })}
      />
      <HistoryAndPropertyModal
        objectTypeSysname="destination"
        objectId={objectId}
        handleHideModal={() => setHistoryModalVisible(false)}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_TC_ADMIN_DESTINATIONS_WRITE)}
      />
    </div>
  );
};
